import type { PermissionCode, RoleCode } from '@cnss/shared';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import { apiClient, setAccessToken } from './api-client';

export interface SessionUser {
  id: number;
  uuid: string;
  nomComplet: string;
  username: string;
  role: RoleCode;
  permissions: PermissionCode[];
  mustChangePassword: boolean;
}

interface LoginResult {
  requiresTwoFactor: boolean;
  requiresTwoFactorSetup: boolean;
  challengeToken?: string;
}

interface AuthContextValue {
  user: SessionUser | null;
  status: 'loading' | 'authenticated' | 'unauthenticated';
  login: (username: string, password: string) => Promise<LoginResult>;
  completeTwoFactor: (challengeToken: string, code: string) => Promise<void>;
  /** Termine un provisionnement 2FA forcé en cours de connexion (voir TwoFactorSetupPage). */
  completeTwoFactorSetup: (challengeToken: string, code: string) => Promise<void>;
  logout: () => Promise<void>;
  hasPermission: (code: PermissionCode) => boolean;
  /** Recharge `/auth/me` — à appeler après un changement de mot de passe. */
  refetchUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

/**
 * Contrat attendu du back-office (Sprint 1) :
 *   POST /auth/login          { username, password } -> { requiresTwoFactor, requiresTwoFactorSetup, challengeToken?, accessToken?, user? }
 *   POST /auth/2fa/verify     { challengeToken, code } -> { accessToken, user }
 *   POST /auth/refresh        (cookie)                 -> { accessToken }
 *   GET  /auth/me                                      -> { user }
 *   POST /auth/logout
 *   POST /auth/change-password { currentPassword, newPassword }
 *   POST /auth/2fa/setup       { challengeToken? } -> { otpauthUrl, qrCodeDataUrl, manualEntryKey }
 *   POST /auth/2fa/enable      { code, challengeToken? } -> { success: true } | { accessToken, user }
 *
 * `requiresTwoFactorSetup=true` (rôle exigeant la 2FA, pas encore active) ne
 * s'accompagne JAMAIS d'un `accessToken` : l'API n'émet une session complète
 * qu'une fois la 2FA effectivement activée (voir /2fa/enable ci-dessus), pour
 * qu'un mot de passe seul ne suffise jamais à obtenir un accès privilégié.
 * `LoginPage` enchaîne alors sur `TwoFactorSetupPage` avec ce `challengeToken`.
 * `user.mustChangePassword` reste la seule garde de post-connexion appliquée
 * par `RequireAuth` (apps/web/src/routes/RequireAuth.tsx).
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [status, setStatus] = useState<AuthContextValue['status']>('loading');

  useEffect(() => {
    apiClient
      .post<{ accessToken: string }>('/auth/refresh')
      .then(async ({ data }) => {
        setAccessToken(data.accessToken);
        const me = await apiClient.get<{ user: SessionUser }>('/auth/me');
        setUser(me.data.user);
        setStatus('authenticated');
      })
      .catch(() => {
        setStatus('unauthenticated');
      });
  }, []);

  const login = useCallback(async (username: string, password: string): Promise<LoginResult> => {
    const { data } = await apiClient.post<{
      requiresTwoFactor: boolean;
      requiresTwoFactorSetup: boolean;
      challengeToken?: string;
      accessToken?: string;
      user?: SessionUser;
    }>('/auth/login', { username, password });

    if (data.requiresTwoFactor || data.requiresTwoFactorSetup) {
      return {
        requiresTwoFactor: data.requiresTwoFactor,
        requiresTwoFactorSetup: data.requiresTwoFactorSetup,
        challengeToken: data.challengeToken,
      };
    }

    if (data.accessToken && data.user) {
      setAccessToken(data.accessToken);
      setUser(data.user);
      setStatus('authenticated');
    }
    return { requiresTwoFactor: false, requiresTwoFactorSetup: false };
  }, []);

  const completeTwoFactor = useCallback(async (challengeToken: string, code: string) => {
    const { data } = await apiClient.post<{ accessToken: string; user: SessionUser }>(
      '/auth/2fa/verify',
      {
        challengeToken,
        code,
      },
    );
    setAccessToken(data.accessToken);
    setUser(data.user);
    setStatus('authenticated');
  }, []);

  const completeTwoFactorSetup = useCallback(async (challengeToken: string, code: string) => {
    const { data } = await apiClient.post<{ accessToken: string; user: SessionUser }>(
      '/auth/2fa/enable',
      { challengeToken, code },
    );
    setAccessToken(data.accessToken);
    setUser(data.user);
    setStatus('authenticated');
  }, []);

  const logout = useCallback(async () => {
    await apiClient.post('/auth/logout').catch(() => undefined);
    setAccessToken(null);
    setUser(null);
    setStatus('unauthenticated');
  }, []);

  const hasPermission = useCallback(
    (code: PermissionCode) => user?.permissions.includes(code) ?? false,
    [user],
  );

  const refetchUser = useCallback(async () => {
    const { data } = await apiClient.get<{ user: SessionUser }>('/auth/me');
    setUser(data.user);
  }, []);

  const value = useMemo(
    () => ({
      user,
      status,
      login,
      completeTwoFactor,
      completeTwoFactorSetup,
      logout,
      hasPermission,
      refetchUser,
    }),
    [
      user,
      status,
      login,
      completeTwoFactor,
      completeTwoFactorSetup,
      logout,
      hasPermission,
      refetchUser,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth doit être utilisé à l'intérieur de <AuthProvider>");
  }
  return ctx;
}
